// Воспроизводит auth-механику AngularJS-кабинета бит-в-бит:
// - phpBuildQuery: эмуляция PHP http_build_query (escape + %20→+), см. legacy authorization.js
// - extractPathParams: если последний сегмент URL — число, добавляем {id: N}
//   (компенсация Yii urlManager-правила <controller>/<action>/<id:\d+>)
// - signRequest: serviceId+timestamp+http_build_query(params), HMAC-SHA256(authKey), base64
// Полное описание граблей: project_auth_redesign.md.

export type SignParams = Record<string, string | number | boolean | undefined>;

// Эмуляция PHP urlencode (RFC 1738), которым http_build_query кодирует и ключи,
// и значения: не кодируются только [A-Za-z0-9-_.], пробел уходит в '+'.
//
// Раньше здесь стоял легаси `escape()` из authorization.js — и он расходится с
// PHP на всём, что вне ASCII: `escape('ваы')` даёт '%u0432%u0430%u044B' (UTF-16),
// а PHP — '%D0%B2%D0%B0%D1%8B' (UTF-8). Подпись не совпадала, бек отвечал 401, а
// общий error-интерсептор трактовал это как «сессия невалидна» и разлогинивал —
// достаточно было написать кириллицу в поиск лендингов. Тем же путём врали `+`,
// `@` и `*`: escape их вообще не кодирует, PHP кодирует.
//
// encodeURIComponent оставляет !~*'() — добиваем их вручную, как делает urlencode.
function phpUrlencode(value: string): string {
  return encodeURIComponent(value)
    .replace(/[!~*'()]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`)
    .replace(/%20/g, '+');
}

export function phpBuildQuery(params: SignParams): string {
  const parts: string[] = [];
  for (const [rawKey, rawVal] of Object.entries(params)) {
    if (rawVal === undefined) continue;
    parts.push(`${phpUrlencode(rawKey)}=${phpUrlencode(String(rawVal))}`);
  }
  return parts.join('&');
}

export function extractPathId(url: string): number | null {
  const pathOnly = url.split('?')[0];
  const lastSegment = pathOnly.split('/').pop() ?? '';
  const parsed = parseInt(lastSegment, 10);
  return Number.isInteger(parsed) && String(parsed) === lastSegment ? parsed : null;
}

// Значения из query-строки декодируем: подписывать нужно то же, что увидит в
// $_GET бек (декодированное), иначе phpBuildQuery закодирует их второй раз и
// '%D0%B2' станет '%25D0%25B2'. Невалидную последовательность оставляем как есть —
// сломанная подпись лучше исключения на весь запрос.
function decodeQueryPart(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

export function parseQueryString(url: string): SignParams {
  const queryIdx = url.indexOf('?');
  if (queryIdx === -1) return {};
  const out: SignParams = {};
  for (const pair of url.slice(queryIdx + 1).split('&')) {
    if (!pair) continue;
    const [key, val = ''] = pair.split('=');
    out[decodeQueryPart(key)] = decodeQueryPart(val);
  }
  return out;
}

import HmacSHA256 from 'crypto-js/hmac-sha256';
import Base64 from 'crypto-js/enc-base64';

// Используем crypto-js, а не Web Crypto API:
// crypto.subtle доступен только в Secure Contexts (HTTPS / localhost),
// а кабинет крутится на http://partner.lc:<port> — там crypto.subtle === undefined.
function hmacSha256Base64(key: string, message: string): string {
  return Base64.stringify(HmacSHA256(message, key));
}

export interface BuildAuthHeaderInput {
  url: string;
  authKey: string;
  serviceId: number | string;
  timestamp: number;
  extraParams?: SignParams;
}

export function buildAuthHeader(input: BuildAuthHeaderInput): {
  header: string;
  finalParams: SignParams;
} {
  const { url, authKey, serviceId, timestamp, extraParams = {} } = input;
  const queryParams = parseQueryString(url);
  const pathId = extractPathId(url);
  // Порядок ключей важен: серверный $_GET (Yii UrlManager) кладёт path-id ПЕРВЫМ,
  // потом query, потом то, что прилетело отдельно (lang-id и т.п.).
  // http_build_query сериализует в этом порядке — клиент обязан повторить.
  const finalParams: SignParams = {};
  if (pathId !== null) {
    finalParams.id = pathId;
  }
  Object.assign(finalParams, queryParams, extraParams);
  const data = `${serviceId}${timestamp}${phpBuildQuery(finalParams)}`;
  const signature = hmacSha256Base64(authKey, data);
  return {
    header: `${serviceId}:${timestamp}:${signature}`,
    finalParams,
  };
}
